// StartCustomCode - Delivery estimate widget
(function () {
  'use strict';

  var STORAGE_KEY = 'ohclocks_delivery_location';
  var WORKER_URL = '';
  var AUTO_TTL_MS = 30 * 24 * 60 * 60 * 1000; // auto-detected locations expire after 30 days
  var activeAbortController = null;
  var requestSeq = 0;

  // ===== OHC shipping-charge signal =====
  // When shipping-margin charging is live the Bespoke macro can return a non-zero standard
  // rate. Shipping protection must not be stacked on top of a delivery the customer is now
  // paying for separately, so the cart quote broadcasts whether standard shipping costs
  // money and assets/shipping-protection.js removes (or restores) its line item.
  var OHC_SUPPRESS_KEY = 'ohc-sp-suppressed';
  var OHC_FORCE_KEY = 'ohc-force-charge';
  var OHC_FORCE_SUPPRESS_KEY = 'ohc-sp-preview-suppressed';

  // Reject malformed rate data before caching, rendering or changing protection.
  function ohcRateAmount(rate) {
    if (rate.price !== undefined) return Number(rate.price) / 100;
    return Number(rate.priceDollars);
  }

  function ohcValidRate(rate) {
    if (!rate || typeof rate !== 'object' || Array.isArray(rate)) return false;
    var name = rate.serviceName !== undefined ? rate.serviceName : rate.service_name;
    if (typeof name !== 'string' || !name.trim()) return false;
    if (rate.serviceCode !== undefined && typeof rate.serviceCode !== 'string') return false;
    if (rate.service_code !== undefined && typeof rate.service_code !== 'string') return false;
    if (rate.description !== undefined && typeof rate.description !== 'string') return false;
    if (typeof rate.isFree !== 'boolean') return false;
    var cents = rate.price, dollars = rate.priceDollars;
    if (cents === undefined && dollars === undefined) return false;
    if (cents !== undefined) {
      if (typeof cents !== 'number' && !(typeof cents === 'string' && /^\d+$/.test(cents))) return false;
      cents = Number(cents);
      if (!isFinite(cents) || cents < 0 || Math.floor(cents) !== cents || cents > 9007199254740991) return false;
    }
    if (dollars !== undefined) {
      if (typeof dollars !== 'number' && !(typeof dollars === 'string' && /^\d+(\.\d{1,2})?$/.test(dollars))) return false;
      dollars = Number(dollars);
      if (!isFinite(dollars) || dollars < 0 || dollars * 100 > 9007199254740991 ||
          Math.abs(dollars * 100 - Math.round(dollars * 100)) > 0.00001) return false;
      if (cents !== undefined && Math.abs(cents - dollars * 100) > 0.00001) return false;
    }
    return rate.isFree === (ohcRateAmount(rate) === 0);
  }

  function ohcValidRates(rates) {
    if (!Array.isArray(rates) || !rates.length) return false;
    for (var i = 0; i < rates.length; i++) if (!ohcValidRate(rates[i])) return false;
    return true;
  }

  function ohcValidatedQuote(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    var issue = quoteIssue(data);
    // Temporary quote-service failures use general estimates (or a known cached result).
    // Keep exclusions and unrecognised pricing/contract failures on their guarded path.
    if (isTemporaryQuoteFailure(data)) return null;
    // Policy evidence needs no rates. Cache only a minimal, safe policy envelope.
    if (issue) {
      var policy = { status: issue === 'excluded' ? 'excluded' : 'unavailable', rates: [] };
      if (issue === 'unavailable' && typeof data.reason === 'string' && data.reason) policy.reason = data.reason;
      return policy;
    }
    if (data.rates !== undefined && !Array.isArray(data.rates)) return null;
    if (data.rates && data.rates.length && !ohcValidRates(data.rates)) return null;
    var special = data.specialOrder;
    if (special && !((special === true && typeof data.message === 'string') ||
        (typeof special === 'object' && !Array.isArray(special) && typeof special.message === 'string'))) return null;
    if (!special && !ohcValidRates(data.rates)) return null;
    return data;
  }

  // Explicit store service labels win. Opaque service codes carry no tier.
  // Tony confirmed only Allied Express Standard is enabled; Same Day is Priority/off.
  // The default mapping covers the known current names, not every Allied service.
  function ohcRateTier(rate) {
    if (!rate || typeof rate !== 'object') return 'unknown';
    var name = rate.serviceName !== undefined ? rate.serviceName : rate.service_name;
    if (typeof name !== 'string') return 'unknown';
    // Priority/Same Day must never establish Standard eligibility, even with a conflicting code.
    if (/\bpriority\b|\bsame[\s-]+day\b/i.test(name)) return 'priority';
    var code = rate.serviceCode !== undefined ? rate.serviceCode : rate.service_code;
    if (typeof code === 'string') {
      code = code.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
      if (/^standard(?: shipping| delivery)?$/.test(code)) return 'standard';
      if (/^express(?: shipping| delivery)?$/.test(code)) return 'express';
      if (/^(?:priority|same day)(?: shipping| delivery)?$/.test(code)) return 'priority';
    }
    var parts = name.toLowerCase().split('+'), standard = false, express = false;
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim().replace(/\s+/g, ' ');
      if (/\bpriority\b|\bsame[ -]day\b/.test(part)) return 'priority';
      var label = part.replace(/\ballied express\b/g, '');
      if (/\bstandard\b/.test(label)) standard = true;
      if (/\bexpress\b/.test(label)) express = true;
      if (/\bstandard\b|\bexpress\b/.test(label)) continue;
      if (/^allied express(?: overnight)?$/.test(part)) standard = true;
      else if (/\bexpress\b/.test(part)) express = true;
      else standard = true;
    }
    return standard && express ? 'mixed' : express ? 'express' : 'standard';
  }

  function ohcStandardRateIndex(rates) {
    if (!ohcValidRates(rates)) return -1;
    for (var i = 0; i < rates.length; i++) {
      var tier = ohcRateTier(rates[i]);
      if (tier === 'standard' || tier === 'mixed') return i;
    }
    return -1;
  }

  // Preview-only display test; it does NOT change checkout. Gate on role, not ID:
  // publishing the copy retains its ID. Unknown/missing roles must fail closed.
  // `?ohc_force_charge=12.40` persists for this session; `=0` clears it.
  function ohcForcedCharge() {
    try {
      var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
      if (role !== 'unpublished' && role !== 'development') {
        // Also remove suppression caused by a preview price, not genuine paid shipping.
        // The force key covers sessions created before the preview marker existed.
        if (sessionStorage.getItem(OHC_FORCE_SUPPRESS_KEY) || sessionStorage.getItem(OHC_FORCE_KEY)) {
          sessionStorage.removeItem(OHC_SUPPRESS_KEY);
        }
        sessionStorage.removeItem(OHC_FORCE_SUPPRESS_KEY);
        sessionStorage.removeItem(OHC_FORCE_KEY);
        return null;
      }
      var m = /[?&]ohc_force_charge=([^&]*)/.exec(window.location.search);
      if (m) {
        if (/^\d+(\.\d{1,2})?$/.test(m[1]) && Number(m[1]) > 0 && isFinite(Number(m[1]))) {
          sessionStorage.setItem(OHC_FORCE_KEY, m[1]);
        }
        else sessionStorage.removeItem(OHC_FORCE_KEY);
      }
      var v = sessionStorage.getItem(OHC_FORCE_KEY);
      return v && /^\d+(\.\d{1,2})?$/.test(v) && isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null;
    } catch (e) { return null; }
  }
  // Clear a preview's stale override immediately, even if no quote runs on this page.
  ohcForcedCharge();

  // Rewrite the standard rate in place so the label, the price and the protection signal all
  // tell one consistent story rather than diverging under test.
  function ohcApplyForced(rates) {
    var forced = ohcForcedCharge();
    if (forced === null || !ohcValidRates(rates)) return rates;
    var idx = ohcStandardRateIndex(rates);
    if (idx !== -1) {
      rates[idx].isFree = false;
      rates[idx].price = Math.round(forced * 100);
      // Same formatting the Worker uses (formatRates / blendGroupRates), so a simulated charge
      // renders character-for-character like a real one — "12", not "12.00".
      rates[idx].priceDollars = forced.toFixed(2).replace(/\.00$/, '');
      rates[idx].hideFallbackPrice = false;
    }
    return rates;
  }

  // Only an actual free Standard rate supports a free-shipping heading.
  // Missing, special-order and failed quotes must not promise a free delivery.
  function ohcHeadingLead(rates) {
    var idx = rates && rates.length ? ohcStandardRateIndex(rates) : -1;
    if (idx === -1 || rates[idx].hideFallbackPrice || !rates[idx].isFree) return 'Shipping ';
    return '<span class="de__heading-emphasis">FREE Shipping</span> ';
  }

  // Broadcast whether standard shipping is charged. Persisted to sessionStorage because the
  // protection engine's init() syncs the cart BEFORE the first quote returns - on a fresh
  // page load it has to be able to read the previous page's answer synchronously, or it
  // re-adds protection for a moment before the quote arrives to take it away again.
  // ONLY the cart quote calls this: a PDP quote covers one item and is not authoritative for
  // a multi-line cart.
  function ohcSignalCharge(rates) {
    var charged = false, amount = 0;
    var idx = rates ? ohcStandardRateIndex(rates) : -1;
    // An Express-only response does not establish whether Standard became free.
    if (idx === -1 || rates[idx].hideFallbackPrice) return;
    if (!rates[idx].isFree) {
      charged = true;
      amount = ohcRateAmount(rates[idx]);
    }
    var simulated = charged && ohcForcedCharge() !== null;
    try {
      if (charged) sessionStorage.setItem(OHC_SUPPRESS_KEY, '1');
      else sessionStorage.removeItem(OHC_SUPPRESS_KEY);
      if (simulated) sessionStorage.setItem(OHC_FORCE_SUPPRESS_KEY, '1');
      else sessionStorage.removeItem(OHC_FORCE_SUPPRESS_KEY);
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('ohc:shipping-charge', {
      detail: { charged: charged, amount: amount }
    }));
  }

  // Shrinks the "to [chip] Change" cluster to fit the cart widget's actual
  // width, instead of wrapping. Container width doesn't track viewport width
  // (sidebar vs. full-width drawer), so a CSS vw-based clamp can't do this -
  // it has to be measured against the real element.
  function fitCartLocGroup(container) {
    var el = container.querySelector('.de__loc-group');
    if (!el) return;
    var maxSize = 15;
    var minSize = 11;
    var available = container.clientWidth;
    if (!available) return;
    var size = maxSize;
    el.style.fontSize = size + 'px';
    while (el.getBoundingClientRect().width > available && size > minSize) {
      size -= 0.5;
      el.style.fontSize = size + 'px';
    }
  }

  function getSavedLocation() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var loc = JSON.parse(raw);
      // Auto-detected (IP) locations expire; manual selections never do.
      if (loc && loc.auto && (!loc.savedAt || Date.now() - loc.savedAt > AUTO_TTL_MS)) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return loc;
    } catch (e) {
      return null;
    }
  }

  function saveLocation(loc, isAuto) {
    try {
      var toStore = loc;
      if (isAuto) {
        toStore = { suburb: loc.suburb, state: loc.state, postcode: loc.postcode, country: loc.country, auto: true, savedAt: Date.now() };
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch (e) { /* ignore */ }
  }

  var SAME_DAY_VENDORS = ['one six eight london', 'toki', 'clocksicle'];

  var STATIC_FALLBACK = {
    AU: [{ serviceName: 'Standard', description: 'Metro: 2–6 business days<br>Regional: 5–10 business days', isFree: true, priceDollars: '0', hideFallbackPrice: true }],
    NZ: [{ serviceName: 'Standard', description: '6–10 business days', isFree: true, priceDollars: '0', hideFallbackPrice: true }]
  };
  var FALLBACK_NOTE_AU = 'General estimates only. Check Standard and Express options, shipping charges and delivery dates at checkout.';
  var FALLBACK_NOTE_NZ = 'General estimates only. Confirm delivery dates at checkout.';

  function fallbackOfferHeading(country) {
    return '<div class="de__heading">' + (country === 'NZ'
      ? '<strong class="de__heading-offer">Free shipping NZ-wide</strong>'
      : '<strong class="de__heading-offer">Free shipping</strong> to most AU metro areas') + '</div>';
  }

  function getFallbackRates(country) {
    return STATIC_FALLBACK[country] || STATIC_FALLBACK.AU;
  }

  // A policy exclusion is different from an unavailable estimate or a network error.
  // Recognize the prepared tariff envelope as well as direct endpoint responses.
  function quoteIssue(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    var tariff = data.publishedTariff;
    if (!tariff || typeof tariff !== 'object' || Array.isArray(tariff)) tariff = {};
    var status = data.status || tariff.status || '';
    var reason = data.reason || data.error || '';
    if (status === 'excluded' || status === 'no_delivery' ||
        reason === 'excluded_destination' || reason === 'no_delivery' ||
        data.noShipping === true || tariff.noShipping === true) return 'excluded';
    if (status === 'blocked' || status === 'unresolved' || status === 'unavailable') return 'unavailable';
    return null;
  }

  function isTemporaryQuoteFailure(data) {
    return quoteIssue(data) === 'unavailable' &&
      /^scoped_(?:upstream_(?:unavailable|error|no_rates)|timeout)$/.test(data.reason || '');
  }

  function locationIdentity(loc) {
    return loc ? JSON.stringify([loc.country || 'AU', loc.postcode || '', loc.state || '', loc.suburb || '']) : '';
  }

  function quoteLocationChanged(loc, savedAtRequest) {
    var current = locationIdentity(getSavedLocation());
    return current !== savedAtRequest && current !== locationIdentity(loc);
  }

  function quoteIssueMessage(issue) {
    return issue === 'excluded'
      ? 'We don\'t deliver this order to this destination. Please choose another postcode.'
      : 'Delivery options are not available yet. Check availability and shipping charges at checkout.';
  }

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  function showState(widget, state) {
    hide(widget.querySelector('[data-de-prompt]'));
    hide(widget.querySelector('[data-de-result]'));
    hide(widget.querySelector('[data-de-loading]'));
    hide(widget.querySelector('[data-de-error]'));
    hide(widget.querySelector('[data-de-unavailable]'));
    show(widget.querySelector('[data-de-' + state + ']'));
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ── Quote fetch (longer timeout + one retry) + PDP result cache ──

  var QUOTE_TIMEOUT_MS = 8000;              // was 5000 — don't fall back on a merely-slow response
  var RESULT_CACHE_PREFIX = 'ohclocks_de_pdp_';
  var pendingQuotes = Object.create(null);
  var recentQuotes = Object.create(null);

  function quoteReuseDeadline(data, duration) {
    var now = Date.now();
    var deadline = Math.min(now + duration, (Math.floor(now / 900000) + 1) * 900000);
    if (data && typeof data.quoteCacheExpiresAt === 'number') deadline = Math.min(deadline, data.quoteCacheExpiresAt);
    return deadline;
  }

  // POST a quote; retry once on timeout/error/5xx before giving up. cb(data) or cb(null).
  function fetchQuote(url, payload, cb) {
    var key = JSON.stringify([url, payload]);
    function deliver(callback, data) {
      // Each consumer gets its own copy: preview formatting must not mutate shared rates.
      setTimeout(function () { callback(data ? JSON.parse(JSON.stringify(data)) : null); }, 0);
    }
    var recent = recentQuotes[key];
    if (recent && recent.expiresAt > Date.now()) { deliver(cb, recent.data); return; }
    if (pendingQuotes[key]) { pendingQuotes[key].push(cb); return; }
    pendingQuotes[key] = [cb];
    function finish(data) {
      var valid = ohcValidatedQuote(data);
      var success = valid && valid.rates && ohcStandardRateIndex(valid.rates) >= 0 &&
        !valid.fallback && !/fallback|emergency/i.test(valid.estimateSource || '') &&
        !valid.rates.some(function (rate) { return rate.fallback || rate.hideFallbackPrice; });
      if (success || !data || isTemporaryQuoteFailure(data)) {
        // Short browser reuse; the Worker owns the shared 15-minute quote cache.
        recentQuotes[key] = { data: data ? JSON.parse(JSON.stringify(data)) : null,
          expiresAt: quoteReuseDeadline(data, success ? 60000 : 30000) };
        var keys = Object.keys(recentQuotes);
        if (keys.length > 64) delete recentQuotes[keys[0]];
      }
      var waiting = pendingQuotes[key];
      delete pendingQuotes[key];
      waiting.forEach(function (callback) { deliver(callback, data); });
    }
    var attempts = 0;
    function attempt() {
      attempts++;
      var abort = new AbortController();
      var timer = setTimeout(function () { abort.abort(); }, QUOTE_TIMEOUT_MS);
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: abort.signal
      })
        .then(function (r) {
          clearTimeout(timer);
          return r.json().then(function (data) {
            // Keep explicit policy results, including 4xx exclusions, instead of
            // converting them into a transient error and retrying a blocked route.
            if (!r.ok && !quoteIssue(data)) throw new Error('http ' + r.status);
            return data;
          });
        })
        .then(function (data) { finish(data); })
        .catch(function () {
          clearTimeout(timer);
          if (attempts < 2) { attempt(); } else { finish(null); }
        });
    }
    attempt();
  }

  function localDayKey(d) {
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }

  // Display cache includes all current shipping inputs and expires within 15 minutes.
  // Policy exclusions retain their existing same-day persistence and are revalidated.
  function pdpCacheKey(variantId, location, currency, itemIdentity) {
    return RESULT_CACHE_PREFIX + JSON.stringify([variantId, locationIdentity(location), currency, itemIdentity]);
  }

  function getCachedPdpResult(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.data) return null;
      if (entry.day !== localDayKey(new Date())) { localStorage.removeItem(key); return null; }
      var data = ohcValidatedQuote(entry.data);
      if (!data) { localStorage.removeItem(key); return null; }
      if (data.rates && data.rates.length) {
        if (typeof entry.expiresAt !== 'number' || !isFinite(entry.expiresAt) || entry.expiresAt <= Date.now()) {
          localStorage.removeItem(key); return null;
        }
        data.quoteCacheExpiresAt = Math.min(entry.expiresAt, typeof data.quoteCacheExpiresAt === 'number' ? data.quoteCacheExpiresAt : entry.expiresAt);
      }
      return data;
    } catch (e) { return null; }
  }

  function setCachedPdpResult(key, data) {
    data = ohcValidatedQuote(data);
    if (!data) return;
    try {
      localStorage.setItem(key, JSON.stringify({ data: data, day: localDayKey(new Date()), expiresAt:quoteReuseDeadline(data, 900000) }));
    } catch (e) { /* ignore quota errors */ }
  }

  // ── IP geolocation (via Cloudflare Worker /geo, validated server-side) ──

  function detectLocation(cb) {
    if (!WORKER_URL) { cb(null); return; }
    var abort = new AbortController();
    var timeout = setTimeout(function () { abort.abort(); }, 5000);
    fetch(WORKER_URL + '/geo', { signal: abort.signal })
      .then(function (r) { return r.json(); })
      .then(function (loc) {
        clearTimeout(timeout);
        if (loc && loc.suburb && loc.postcode && loc.country) {
          cb(loc);
        } else {
          cb(null);
        }
      })
      .catch(function () { clearTimeout(timeout); cb(null); });
  }

  // Keep manual entry usable during IP lookup; typing or selecting always wins.
  function detectLocationUnlessManual(container, callback) {
    var cancelled = false;
    var input = container.querySelector('[data-de-input]');
    function cancel() { cancelled = true; }
    if (input) input.addEventListener('input', cancel);
    detectLocation(function (loc) {
      if (input) input.removeEventListener('input', cancel);
      if (cancelled || getSavedLocation() || container.isConnected === false) return;
      callback(loc);
    });
    return cancel;
  }
  // ── Delivery date formatting ──
  // Worker returns e.g. "estimated Fri 17 Jul - Mon 20 Jul". We relabel to
  // "Expected delivery" and collapse a shared month ("Fri 17 - Mon 20 Jul").
  // Fallback strings (e.g. "Metro: 2–6 business days") are passed through as raw.

  function formatDeliveryDate(description) {
    var desc = (description || '').trim();
    if (!/^estimated\b/i.test(desc)) {
      return { raw: description || '' };
    }
    var body = desc.replace(/^estimated\s+/i, '');
    var parts = body.split(' - ');
    if (parts.length !== 2) {
      return { label: 'Expected delivery', range: body };
    }
    var a = parts[0].trim().split(/\s+/); // [dow, day, mon]
    var b = parts[1].trim().split(/\s+/);
    var range;
    if (a.length === 3 && b.length === 3 && a[2] === b[2]) {
      // same month → show month once on the second date only
      range = a[0] + ' ' + a[1] + ' - ' + b[0] + ' ' + b[1] + ' ' + b[2];
    } else {
      range = parts[0].trim() + ' - ' + parts[1].trim();
    }
    return { label: 'Expected delivery', range: range };
  }

  // Build the two-line "when" markup from a rate description.
  function buildWhenHtml(description) {
    var f = formatDeliveryDate(description);
    if (f.raw !== undefined) {
      return '<div class="de__rate-when de__rate-when--raw">' + escapeHtml(f.raw) + '</div>';
    }
    return '<div class="de__rate-when">' +
      '<span class="de__exp">' + f.label + '</span> ' +
      '<span class="de__range">' + escapeHtml(f.range) + '</span></div>';
  }

  // ── Autocomplete ──

  function fetchSuggestions(query, callback) {
    if (query.length < 2 || !WORKER_URL) { callback([]); return; }

    if (activeAbortController) activeAbortController.abort();
    activeAbortController = new AbortController();
    var seq = ++requestSeq;
    var signal = activeAbortController.signal;

    var timeout = setTimeout(function () { activeAbortController.abort(); }, 5000);

    fetch(WORKER_URL + '/suburbs?q=' + encodeURIComponent(query), { signal: signal })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        clearTimeout(timeout);
        if (seq === requestSeq) callback(data || []);
      })
      .catch(function () { clearTimeout(timeout); });
  }

  function renderSuggestions(list, suggestions, onSelect) {
    list.innerHTML = '';
    if (!suggestions.length) { hide(list); return; }

    suggestions.forEach(function (s) {
      var li = document.createElement('li');
      li.className = 'de__suggestion';
      li.setAttribute('role', 'option');
      li.textContent = s.suburb + ', ' + s.state + ' ' + s.postcode + (s.country === 'NZ' ? ' NZ' : '');
      li.addEventListener('click', function () {
        onSelect(s);
        hide(list);
      });
      list.appendChild(li);
    });
    show(list);
  }

  function setupAutocomplete(container, onSelect) {
    var input = container.querySelector('[data-de-input]');
    var list = container.querySelector('[data-de-suggestions]');
    if (!input || !list) return;

    var debounceTimer = null;
    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        fetchSuggestions(q, function (results) {
          renderSuggestions(list, results, function (loc) {
            input.value = loc.suburb + ' ' + loc.state + ' ' + loc.postcode;
            onSelect(loc);
          });
        });
      }, 250);
    });

    input.addEventListener('keydown', function (e) {
      var items = list.querySelectorAll('.de__suggestion');
      var current = list.querySelector('[aria-selected="true"]');
      var idx = Array.prototype.indexOf.call(items, current);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (current) current.removeAttribute('aria-selected');
        var next = items[idx + 1] || items[0];
        if (next) next.setAttribute('aria-selected', 'true');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (current) current.removeAttribute('aria-selected');
        var prev = items[idx - 1] || items[items.length - 1];
        if (prev) prev.setAttribute('aria-selected', 'true');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (current) current.click();
      } else if (e.key === 'Escape') {
        hide(list);
      }
    });

    document.addEventListener('click', function (e) {
      if (!container.contains(e.target)) hide(list);
    });
  }

  // ── Rate row rendering (shared two-line layout, PDP + cart) ──

  // Pre-order caveat (cart only).
  // A PDP for a pre-order vendor renders no widget at all (delivery-estimate.liquid), and neither
  // does an all-pre-order cart — the quote is transit-only and assumes we ship today, so it would
  // promise delivery before the dispatch date. A MIXED cart still shows the estimate, because it's
  // accurate for the rest of the order, but has to say what it doesn't cover. Deliberately names no
  // date: each line already shows its own, and different vendors can be on different dates.
  // Flagged by data-has-preorder, set in Liquid from snippets/preorder.date.liquid.
  function preorderNoteHtml(el, cls) {
    if (!el || !el.getAttribute('data-has-preorder')) return '';
    return '<div class="' + cls + '">Excludes pre-order items — see the dispatch date on each item</div>';
  }

  function sortRates(rates) {
    if (!ohcValidRates(rates)) return [];
    return rates.slice().sort(function (a, b) {
      var aExp = ohcRateTier(a) === 'standard' || ohcRateTier(a) === 'mixed' ? 0 : 1;
      var bExp = ohcRateTier(b) === 'standard' || ohcRateTier(b) === 'mixed' ? 0 : 1;
      return aExp - bExp;
    });
  }

  // One rate → two-line block: "service (+ express price)" over "Expected delivery <range>".
  // Standard shows no price (its name already says "Free"); Express shows price beside the name.
  //
  // A combined rate that spans both tiers (e.g. Bespoke's checkout-style "Express + Standard"
  // merge for a mixed NZ cart, see Bespoke macro rate_6myipu1mc / blendGroupRates fallback in
  // the Worker) mentions both words — treat that as standard/mixed, not pure express, so we
  // don't over-promise "Free express delivery" when only part of the cart actually qualifies.
  function buildRateHtml(rate) {
    if (!ohcValidRate(rate)) return '';
    var tier = ohcRateTier(rate);
    var isExpress = tier === 'express';
    var isMixed = tier === 'mixed';
    // The standard arm used to be the unconditional string 'Free standard delivery', and the
    // price below was shown only for paid EXPRESS — so a standard rate that cost money would
    // have rendered as "Free standard delivery" with no figure at all. Harmless while every
    // standard rate is free; a lie the moment shipping-margin charging is switched on.
    var serviceName = isMixed
      ? (rate.isFree ? 'Free standard + express delivery' : 'Standard + express delivery')
      : tier === 'priority'
        ? (rate.isFree ? 'Free priority delivery' : 'Priority delivery')
      : isExpress
        ? (rate.isFree ? 'Free express delivery' : 'Express delivery')
        : (rate.isFree ? 'Free standard delivery' : 'Standard delivery');
    // hideFallbackPrice keeps the static offline fallback from inventing a "$0".
    var priceHtml = (!rate.isFree && !rate.hideFallbackPrice)
      ? '<span class="de__price">$' + escapeHtml(ohcRateAmount(rate).toFixed(2).replace(/\.00$/, '')) + '</span>' : '';

    return '<div class="de__rate">' +
      '<div class="de__rate-head">' +
        '<span class="de__service">' + serviceName + '</span>' + priceHtml +
      '</div>' +
      (rate.description ? buildWhenHtml(rate.description) : '') +
      '</div>';
  }

  function buildRateRowsHtml(rates, country) {
    var html = '';
    var hasStandard = false;
    var hasExpress = false;
    var isFallback = false;

    sortRates(rates).forEach(function (rate) {
      var tier = ohcRateTier(rate);
      if (rate.hideFallbackPrice) isFallback = true;
      if (tier === 'express' || tier === 'mixed') hasExpress = true;
      if (tier === 'standard' || tier === 'mixed') hasStandard = true;
      html += buildRateHtml(rate);
    });

    if (!isFallback && hasStandard && !hasExpress && country !== 'NZ') {
      html += '<div class="de__rate de__rate--unavailable">' +
        '<span class="de__service">Express delivery isn\'t available for this item to your postcode.</span></div>';
    }

    return html;
  }

  // ── PDP logic ──

  function initPdp(widget) {
    WORKER_URL = widget.dataset.workerUrl || WORKER_URL;
    var jadcoSpecialSkus = ['BQ1700', 'BQ20', 'BQ368', 'MQ100-12HR', 'MQ100-24HR', 'R100-12HR', 'R100-24HR'];
    var dispatchNote = widget.querySelector('[data-de-dispatch]');
    var pdpQuoteSeq = 0;
    var cancelAutoDetection = function () {};
    var inputWrap = widget.querySelector('.de__input-wrap');
    var reservedWidth = 0;
    var reservedHeight = 0;
    var colourHeightKey = 'ohclocks_de_colour_height';

    // Reserve this widget's actual height, never a fixed/clipped result height.
    // A new width gets a fresh reservation so a tall phone result cannot leave a desktop-sized gap.
    function reserveHeight() {
      var width = widget.getBoundingClientRect().width;
      if (!width || widget.isConnected === false) return;
      if (Math.abs(width - reservedWidth) > 1) {
        widget.style.minHeight = '';
        reservedHeight = 0;
        reservedWidth = width;
        try {
          var transfer = JSON.parse(sessionStorage.getItem(colourHeightKey) || 'null');
          sessionStorage.removeItem(colourHeightKey);
          if (transfer && transfer.expiresAt > Date.now() &&
              transfer.target === window.location.pathname + window.location.search &&
              transfer.currency === widget.dataset.currency &&
              transfer.location === locationIdentity(getSavedLocation()) &&
              Math.abs(transfer.width - width) <= 1 && typeof transfer.height === 'number' &&
              isFinite(transfer.height) && transfer.height > 0) reservedHeight = transfer.height;
        } catch (e) { /* A new page can size naturally when session storage is unavailable. */ }
      }
      reservedHeight = Math.max(reservedHeight, widget.getBoundingClientRect().height);
      widget.style.minHeight = reservedHeight + 'px';
    }

    function showPdpState(state) {
      reserveHeight();
      showState(widget, state);
      reserveHeight();
    }

    function isUnavailable() { return widget.dataset.available === 'false'; }

    function restorePromptInput() {
      if (inputWrap) {
        widget.querySelector('[data-de-prompt]').appendChild(inputWrap);
        show(inputWrap);
      }
    }

    function showUnavailable(location, editing) {
      reserveHeight();
      pdpQuoteSeq++; // Invalidate every outstanding/cached quote consumer immediately.
      hide(dispatchNote);
      widget.classList.remove('delivery-estimate--same-day');
      var locationEl = widget.querySelector('[data-de-soldout-location]');
      var label = location ? escapeHtml(location.suburb) + ' ' + escapeHtml(location.state) + ' ' +
        escapeHtml(location.postcode) + (location.country === 'NZ' ? ' NZ' : '') : '';
      locationEl.innerHTML = location ? 'Delivery location: <button type="button" class="de__chip" data-de-edit-inline>' +
        label + '</button> · <button type="button" class="de__edit-link" data-de-edit-inline>Change</button>' : '';
      var editBtns = locationEl.querySelectorAll('[data-de-edit-inline]');
      for (var i = 0; i < editBtns.length; i++) editBtns[i].addEventListener('click', function () {
        showUnavailable(getSavedLocation(), true);
        var input = widget.querySelector('[data-de-input]');
        if (input) { input.value = ''; input.focus(); }
      });
      if (inputWrap) {
        widget.querySelector('[data-de-unavailable]').appendChild(inputWrap);
        inputWrap.hidden = !!location && !editing;
      }
      showPdpState('unavailable');
    }

    window.addEventListener('resize', reserveHeight);

    function getItemData() {
      var sku = widget.dataset.sku || '';
      var inventoryQty = parseInt(widget.dataset.inventoryQuantity, 10) || 0;
      var inventoryPolicy = widget.dataset.inventoryPolicy || '';
      var outOfStock = inventoryPolicy === 'continue' && inventoryQty < 1 && jadcoSpecialSkus.indexOf(sku) !== -1;

      return {
        name: widget.dataset.title || '',
        sku: sku,
        quantity: 1,
        grams: parseInt(widget.dataset.grams, 10) || 0,
        price: parseInt(widget.dataset.price, 10) || 0,
        vendor: widget.dataset.vendor || '',
        product_id: parseInt(widget.dataset.productId, 10) || 0,
        variant_id: parseInt(widget.dataset.variantId, 10) || 0,
        out_of_stock: outOfStock
      };
    }

    function qualifiesForSameDay() {
      var vendor = (widget.dataset.vendor || '').toLowerCase();
      var qty = parseInt(widget.dataset.inventoryQuantity, 10) || 0;
      if (SAME_DAY_VENDORS.indexOf(vendor) === -1) return false;
      if (qty < 1) return false;
      return true;
    }

    function updateDispatchNote(parentSelector) {
      if (!dispatchNote) return;
      if (!qualifiesForSameDay()) {
        hide(dispatchNote);
        widget.classList.remove('delivery-estimate--same-day');
        return;
      }
      var parent = widget.querySelector(parentSelector);
      if (parent) parent.appendChild(dispatchNote);
      show(dispatchNote);
      widget.classList.add('delivery-estimate--same-day');
    }

    // A fallback has no authoritative rate, so its heading stays neutral.
    function setupResultHeading(location, rates, showFallbackOffer) {
      var heading = widget.querySelector('[data-de-heading]');
      var chipLabel = escapeHtml(location.suburb) + ' ' +
        escapeHtml(location.state) + ' ' + escapeHtml(location.postcode) +
        (location.country === 'NZ' ? ' NZ' : '');
      heading.innerHTML = (showFallbackOffer ? fallbackOfferHeading(location.country) : '') + ohcHeadingLead(rates) +
        '<span class="de__loc-group">to ' +
        '<button type="button" class="de__chip" data-de-edit-inline>' + chipLabel + '</button> ' +
        '<button type="button" class="de__edit-link" data-de-edit-inline>Change</button></span>';
      var editBtns = heading.querySelectorAll('[data-de-edit-inline]');
      for (var i = 0; i < editBtns.length; i++) {
        editBtns[i].addEventListener('click', function () {
          pdpQuoteSeq++;
          showPromptState();
          var input = widget.querySelector('[data-de-input]');
          if (input) { input.value = ''; input.focus(); }
        });
      }
    }

    function showFallback(location) {
      var ratesEl = widget.querySelector('[data-de-rates]');
      var country = location.country || 'AU';
      setupResultHeading(location, null, true);
      var note = country === 'NZ' ? FALLBACK_NOTE_NZ : FALLBACK_NOTE_AU;
      var fallback = STATIC_FALLBACK[country] || STATIC_FALLBACK.AU;
      var desc = fallback[0].description.replace(/<br>/g, '\n').split('\n');
      var html = '<div class="de__body">Live delivery estimates are temporarily unavailable.</div>' +
        '<div class="de__fallback-lines">';
      desc.forEach(function (line) { html += '<div class="de__fallback-line">' + line + '</div>'; });
      html += '</div>';
      html += '<div class="de__fallback-note">' + note + '</div>';
      ratesEl.innerHTML = html;
      hide(dispatchNote);
      widget.classList.remove('delivery-estimate--same-day');
      showPdpState('result');
    }

    function renderPdpResult(data, location) {
      data = ohcValidatedQuote(data);
      var ratesEl = widget.querySelector('[data-de-rates]');
      var issue = quoteIssue(data);
      if (!data || (!issue && !data.specialOrder && (!data.rates || !data.rates.length))) {
        showFallback(location);
        return;
      }
      if (issue) {
        setupResultHeading(location);
        ratesEl.innerHTML = '<div class="de__body">' + quoteIssueMessage(issue || 'unavailable') + '</div>';
        hide(dispatchNote);
        widget.classList.remove('delivery-estimate--same-day');
        showPdpState('result');
        return;
      }
      // Applied at RENDER time — after setCachedPdpResult, so the test override is never baked
      // into the cached quote — but BEFORE the heading is built, which reads the rates to decide
      // whether "FREE Shipping" is still true.
      // Display only: the PDP never signals shipping-protection suppression, because a
      // single-item PDP quote says nothing about what a multi-line cart will be charged.
      ohcApplyForced(data.rates);
      setupResultHeading(location, data.rates);

      if (data.specialOrder) {
        ratesEl.innerHTML = '<div class="de__special-order">' + escapeHtml(data.message) + '</div>';
        showPdpState('result');
        return;
      }

      ratesEl.innerHTML = '<div class="de__rates">' + buildRateRowsHtml(data.rates, location.country) + '</div>';
      showPdpState('result');
      updateDispatchNote('[data-de-result]');
    }

    function fetchEstimate(location) {
      if (isUnavailable()) { showUnavailable(location); return; }
      restorePromptInput();
      var seq = ++pdpQuoteSeq;
      var itemIdentity = JSON.stringify(getItemData());
      var savedAtRequest = locationIdentity(getSavedLocation());
      var currency = widget.dataset.currency || 'AUD';
      var cacheK = pdpCacheKey(widget.dataset.variantId, location, currency, itemIdentity);
      var cachedData = getCachedPdpResult(cacheK);

      if (cachedData) {
        renderPdpResult(cachedData, location);   // instant paint from cache
      } else {
        showPdpState('loading');
        hide(dispatchNote);
      }

      var payload = {
        destination: {
          country: location.country || 'AU',
          postal_code: location.postcode,
          province: location.state || '',
          city: location.suburb || ''
        },
        item: getItemData(),
        currency: currency
      };

      fetchQuote(WORKER_URL + '/pdp-quote', payload, function (data) {
        if (isUnavailable() || seq !== pdpQuoteSeq || itemIdentity !== JSON.stringify(getItemData()) ||
            currentVariantIdFromDom() !== String(widget.dataset.variantId) ||
            quoteLocationChanged(location, savedAtRequest) || widget.isConnected === false) return;
        var temporaryFailure = isTemporaryQuoteFailure(data);
        data = ohcValidatedQuote(data);
        if (data && quoteIssue(data)) {
          // A fresh exclusion/unresolved result replaces a previously cached price.
          // Retain it on later visits until revalidation establishes a valid rate.
          setCachedPdpResult(cacheK, data);
          renderPdpResult(data, location);
          return;
        }
        var ok = data && (data.specialOrder || (data.rates && data.rates.length));
        if (!ok) {
          if (cachedData && typeof cachedData.quoteCacheExpiresAt === 'number' && cachedData.quoteCacheExpiresAt <= Date.now()) cachedData = null;
          // Older caches discarded failure reasons. Replace that generic outage message
          // after a confirmed temporary failure, preserving known policy guards/exclusions.
          if (temporaryFailure && cachedData && quoteIssue(cachedData) === 'unavailable' && !cachedData.reason) {
            try { localStorage.removeItem(cacheK); } catch (e) { /* storage may be unavailable */ }
            cachedData = null;
          }
          if (!cachedData) showFallback(location);   // keep a known price or policy result
          return;
        }
        setCachedPdpResult(cacheK, data);
        // Re-render only if there was no cache shown or the fresh data differs (avoids flicker).
        if (!cachedData || JSON.stringify(cachedData) !== JSON.stringify(data)) {
          renderPdpResult(data, location);
        }
      });
    }

    setupAutocomplete(widget, function (loc) {
      cancelAutoDetection();
      saveLocation(loc);
      fetchEstimate(loc);
    });

    var productSection = widget.closest('[data-section-id]') || widget.closest('.shopify-section');

    // Linked colours navigate to another PDP. Transfer only this measured reservation,
    // once, to that exact destination at the same width/currency/postcode.
    if (productSection) productSection.addEventListener('click', function (event) {
      var link = event.target.closest('ohc-swatches[data-context="product"] a.ohc-polaroid');
      if (!link) return;
      reserveHeight();
      try {
        var target = new URL(link.href, window.location.href);
        sessionStorage.setItem(colourHeightKey, JSON.stringify({
          target: target.pathname + target.search, currency: widget.dataset.currency,
          location: locationIdentity(getSavedLocation()), width: reservedWidth,
          height: reservedHeight, expiresAt: Date.now() + 60000
        }));
      } catch (e) { /* Navigation and quoting do not depend on the reservation. */ }
    });

    // Keep the widget synced to the SELECTED variant. The old code waited for a plain 'variantChange'
    // event that this theme never fires (it uses the scoped 'variant:change:<section>:<product>', and
    // Swatch King uses 'wk:product:change-variant:success'), so the widget stayed frozen on the first
    // variant and quoted its box for every size (e.g. Grey always showed the 31cm rate). Instead we
    // re-read the current variant id from the product form's [name="id"] input, which the native
    // picker AND Swatch King both keep in sync, then look up its grams/sku/price from data-de-variants.
    var _deVariants = null;
    function variantMap() {
      if (_deVariants === null) {
        try { _deVariants = JSON.parse(widget.dataset.deVariants || '{}'); } catch (e) { _deVariants = {}; }
      }
      return _deVariants;
    }
    function currentVariantIdFromDom() {
      var scope = widget.closest('form') || productSection || document;
      var el = (scope && scope.querySelector('[name="id"]')) ||
               document.querySelector('form[action*="/cart/add"] [name="id"]') ||
               document.querySelector('[name="id"]');
      return el ? String(el.value || '') : '';
    }
    function syncVariant(initialOnly) {
      var newId = currentVariantIdFromDom();
      if (!newId || newId === String(widget.dataset.variantId)) return;
      reserveHeight();
      widget.dataset.variantId = newId;
      var vd = variantMap()[newId];
      if (vd) {
        widget.dataset.sku = vd.sku || '';
        widget.dataset.grams = String(vd.grams || 0);
        widget.dataset.price = String(vd.price || 0);
        widget.dataset.inventoryQuantity = String(vd.inventory_quantity || 0);
        widget.dataset.inventoryPolicy = vd.inventory_policy || '';
        widget.dataset.available = String(vd.available);
      }
      var loc = getSavedLocation();
      if (isUnavailable()) { showUnavailable(loc); }
      else if (initialOnly !== true) {
        if (loc) fetchEstimate(loc);
        else showPromptState();
      }
    }

    // Native pickers and Swatch King both finish a variant change with [name="id"] updated and a
    // bubbling 'change'; the explicit Swatch King event is belt-and-braces. syncVariant() no-ops when
    // the id hasn't actually changed, so reacting to every 'change' is cheap.
    document.addEventListener('change', syncVariant);
    document.addEventListener('wk:product:change-variant:success', syncVariant);
    // Align once on load (e.g. a ?variant deep-link where the initial dataset differs from the form).
    syncVariant(true); // The saved-location initialisation below sends the one initial quote.

    function showPromptState() {
      if (isUnavailable()) { showUnavailable(getSavedLocation()); return; }
      restorePromptInput();
      showPdpState('prompt');
      updateDispatchNote('[data-de-prompt]');
    }

    var saved = getSavedLocation();
    if (saved) {
      fetchEstimate(saved);
    } else {
      // No saved location: try IP auto-detection before falling back to manual input.
      showPromptState();
      cancelAutoDetection = detectLocationUnlessManual(widget, function (loc) {
        if (loc) {
          saveLocation(loc, true);
          fetchEstimate(loc);
        } else {
          showPromptState();
        }
      });
    }
  }

  // ── Cart logic (blended order-level estimate) ──

  function initCart() {
    var container = document.querySelector('[data-cart-delivery-estimate]');
    if (!container) return;

    WORKER_URL = container.dataset.workerUrl || WORKER_URL;
    var currency = container.dataset.currency || 'AUD';
    var cartQuoteSeq = 0;
    var cancelAutoDetection = function () {};
    var cartRevision = 0;
    var lastCartQuote = null;
    var lastQuoteLocation = null;
    var lastQuoteSavedLocation = '';
    var cartRefreshPending = false;

    var itemsScript = container.querySelector('[data-cart-items]');
    if (!itemsScript) return;
    var items;
    try { items = JSON.parse(itemsScript.textContent); } catch (e) { return; }
    if (!items.length) return;

    var jadcoSkus = ['BQ1700', 'BQ20', 'BQ368', 'MQ100-12HR', 'MQ100-24HR', 'R100-12HR', 'R100-24HR'];
    items.forEach(function (item) {
      item.out_of_stock = item.inventory_policy === 'continue' &&
        item.inventory_quantity < 1 &&
        jadcoSkus.indexOf(item.sku) !== -1;
    });

    var allSameDay = items.every(function (item) {
      return SAME_DAY_VENDORS.indexOf((item.vendor || '').toLowerCase()) !== -1 &&
        item.inventory_quantity > 0;
    });
    if (allSameDay) container.classList.add('cart-delivery-estimate--same-day');

    container.innerHTML =
      '<div data-cart-de-prompt>' +
        '<div class="de__heading">' + escapeHtml(container.dataset.deInitialHeading || 'Delivery options') + '</div>' +
        '<div class="de__body de__body--cart-sub">' + escapeHtml(container.dataset.deInitialBody || 'Enter your suburb or postcode for shipping rates and delivery estimates.') + '</div>' +
        '<div class="de__input-wrap">' +
          '<input type="text" class="de__input" data-de-input placeholder="Suburb or postcode" autocomplete="off" aria-label="Enter suburb or postcode">' +
          '<ul class="de__suggestions" data-de-suggestions hidden></ul>' +
        '</div>' +
      '</div>' +
      '<div data-cart-de-loading hidden>' +
        '<div class="de__heading de__heading--cart-free">Delivery estimates</div>' +
        '<div class="de__body de__skeleton">Loading delivery estimates...</div>' +
      '</div>' +
      '<div data-cart-de-result hidden></div>' +
      '<div data-cart-de-error hidden>' +
        '<div class="de__heading de__heading--cart-free">Delivery estimates</div>' +
        '<div class="de__body">Couldn\'t load delivery options. Check again at checkout.</div>' +
      '</div>';

    function showCartState(state) {
      hide(container.querySelector('[data-cart-de-prompt]'));
      hide(container.querySelector('[data-cart-de-loading]'));
      hide(container.querySelector('[data-cart-de-result]'));
      hide(container.querySelector('[data-cart-de-error]'));
      show(container.querySelector('[data-cart-de-' + state + ']'));
    }

    function buildCartResultHtml(data, loc) {
      var chipLabel = escapeHtml(loc.suburb) + ' ' + escapeHtml(loc.state) + ' ' + escapeHtml(loc.postcode) +
        (loc.country === 'NZ' ? ' NZ' : '');

      var html = '<div class="de__heading">' + ohcHeadingLead(data.rates) +
        '<span class="de__loc-group">to ' +
        '<button type="button" class="de__chip" data-cart-de-edit>' + chipLabel + '</button> ' +
        '<button type="button" class="de__edit-link" data-cart-de-edit>Change</button></span></div>';

      if (data.specialOrder) {
        html += '<div class="de__special-order">' + escapeHtml(data.specialOrder.message) + '</div>';
      }

      if (data.rates && data.rates.length) {
        var isFallback = data.rates[0] && data.rates[0].hideFallbackPrice;
        html += '<div class="de__rates">';
        sortRates(data.rates).forEach(function (rate) {
          html += buildRateHtml(rate);
        });
        html += '</div>';

        if (!isFallback) {
          var allQualify = items.every(function (item) {
            var v = (item.vendor || '').toLowerCase();
            return SAME_DAY_VENDORS.indexOf(v) !== -1 &&
              item.inventory_quantity > 0;
          });
          if (allQualify) {
            html += '<div class="de__cart-note">Order by 12pm AEST for same business-day dispatch</div>';
          }
          if (data.multipleSuppliers) {
            html += '<div class="de__cart-note">Items may arrive separately</div>';
          }
          if (loc.country !== 'NZ') {
            html += '<div class="de__cart-note">Choose Standard or Express at checkout</div>';
          }
        }
      }

      html += preorderNoteHtml(container, 'de__cart-note');

      return html;
    }

    function showCartFallback(loc, issue) {
      var country = loc.country || 'AU';
      var note = country === 'NZ' ? FALLBACK_NOTE_NZ : FALLBACK_NOTE_AU;
      var chipLabel = escapeHtml(loc.suburb) + ' ' + escapeHtml(loc.state) + ' ' + escapeHtml(loc.postcode) +
        (country === 'NZ' ? ' NZ' : '');
      var fallback = STATIC_FALLBACK[country] || STATIC_FALLBACK.AU;
      var desc = fallback[0].description.replace(/<br>/g, '\n').split('\n');
      var html = (issue ? '' : fallbackOfferHeading(country)) +
        '<div class="de__heading">' + (issue === 'excluded' ? 'Shipping unavailable ' : 'Shipping ') +
        '<span class="de__loc-group">to ' +
        '<button type="button" class="de__chip" data-cart-de-edit>' + chipLabel + '</button> ' +
        '<button type="button" class="de__edit-link" data-cart-de-edit>Change</button></span></div>';
      if (issue) {
        html += '<div class="de__body">' + quoteIssueMessage(issue) + '</div>';
      } else {
        html += '<div class="de__body">Live delivery estimates are temporarily unavailable.</div>';
        html += '<div class="de__fallback-lines">';
        desc.forEach(function (line) { html += '<div class="de__fallback-line">' + line + '</div>'; });
        html += '</div>';
        html += '<div class="de__cart-note">' + note + '</div>';
      }
      html += preorderNoteHtml(container, 'de__cart-note');
      var resultEl = container.querySelector('[data-cart-de-result]');
      resultEl.innerHTML = html;
      showCartState('result');
      fitCartLocGroup(container);
      var editBtns = resultEl.querySelectorAll('[data-cart-de-edit]');
      for (var i = 0; i < editBtns.length; i++) {
        editBtns[i].addEventListener('click', showPrompt);
      }
    }

    function cartDomIdentity() {
      var rows = document.querySelectorAll('.cart__item[data-key]');
      var quantities = [];
      for (var i = 0; i < rows.length; i++) {
        var input = rows[i].querySelector('.element-quantity-selector__input');
        if (input) quantities.push([rows[i].dataset.key, input.value]);
      }
      return JSON.stringify(quantities.sort());
    }

    function fetchCartEstimate(loc) {
      // The pending cart read will quote the newest saved postcode with fresh physical items.
      if (cartRefreshPending) return;
      var seq = ++cartQuoteSeq;
      var revision = cartRevision;
      var domAtRequest = cartDomIdentity();
      var savedAtRequest = locationIdentity(getSavedLocation());
      if (!items.length) { showCartState('prompt'); return; }
      var quoteIdentity = JSON.stringify([locationIdentity(loc), currency, shippingItemsIdentity(items)]);
      lastQuoteLocation = loc;
      lastQuoteSavedLocation = savedAtRequest;
      showCartState('loading');

      var payload = {
        destination: {
          country: loc.country || 'AU',
          postal_code: loc.postcode,
          province: loc.state || '',
          city: loc.suburb || ''
        },
        items: items.map(function (i) {
          return {
            name: i.name, sku: i.sku, quantity: i.quantity, grams: i.grams,
            price: i.price, vendor: i.vendor, product_id: i.product_id,
            variant_id: i.variant_id, out_of_stock: i.out_of_stock
          };
        }),
        currency: currency
      };

      fetchQuote(WORKER_URL + '/cart-quote', payload, function (data) {
        if (seq !== cartQuoteSeq || revision !== cartRevision || domAtRequest !== cartDomIdentity() ||
            quoteLocationChanged(loc, savedAtRequest) ||
            document.querySelector('[data-cart-delivery-estimate]') !== container) return;
        data = ohcValidatedQuote(data);
        var issue = quoteIssue(data);
        if (!data || issue || ((!data.rates || !data.rates.length) && !data.specialOrder)) {
          // Deliberately does NOT clear the suppression flag. A failed quote is not evidence
          // that shipping became free, and re-adding protection on a network blip would be
          // worse than leaving the last known answer in place.
          if (issue) {
            // An explicit policy result replaces any prior quote for this context.
            lastCartQuote = { identity: quoteIdentity, issue: issue };
            showCartFallback(loc, issue);
          } else if (lastCartQuote && lastCartQuote.identity === quoteIdentity) {
            // A transient/empty response cannot erase a known price or exclusion.
            if (lastCartQuote.issue) showCartFallback(loc, lastCartQuote.issue);
            else if (lastCartQuote.expiresAt > Date.now()) {
              container.querySelector('[data-cart-de-result]').innerHTML = lastCartQuote.html;
              showCartState('result');
              fitCartLocGroup(container);
              var retainedBtns = container.querySelector('[data-cart-de-result]').querySelectorAll('[data-cart-de-edit]');
              for (var j = 0; j < retainedBtns.length; j++) retainedBtns[j].addEventListener('click', showPrompt);
            } else showCartFallback(loc);
          } else showCartFallback(loc);
          return;
        }

        // Cart quote only - this is the authoritative one for the whole cart.
        ohcApplyForced(data.rates);
        if (data.rates && data.rates.length) ohcSignalCharge(data.rates);

        var resultEl = container.querySelector('[data-cart-de-result]');
        resultEl.innerHTML = buildCartResultHtml(data, loc);
        lastCartQuote = { identity: quoteIdentity, html: resultEl.innerHTML, expiresAt:quoteReuseDeadline(data, 900000) };
        showCartState('result');
        fitCartLocGroup(container);

        var editBtns = resultEl.querySelectorAll('[data-cart-de-edit]');
        for (var i = 0; i < editBtns.length; i++) {
          editBtns[i].addEventListener('click', showPrompt);
        }
      });
    }

    function showPrompt() {
      cartQuoteSeq++;
      showCartState('prompt');
      var input = container.querySelector('[data-de-input]');
      if (input) { input.value = ''; input.focus(); }
    }

    setupAutocomplete(container, function (loc) {
      cancelAutoDetection();
      saveLocation(loc);
      fetchCartEstimate(loc);
    });

    // Re-fit the location cluster if the cart widget's width changes
    // (drawer open/close, orientation change, window resize).
    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (resizeRaf) return;
      resizeRaf = window.requestAnimationFrame(function () {
        resizeRaf = null;
        fitCartLocGroup(container);
      });
    });

    // Re-quote when physical shipping inputs change; protection affects only the cart total.
    function shippingItemsFromCart(cartItems) {
      if (!cartItems) return [];
      var rebuilt = [];
      cartItems.forEach(function (ci) {
        if (!ci.requires_shipping) return;
        if (ci.product_type === 'Gift Cards') return;
        if (ci.handle === 'navidium-shipping-protection' ||
            (ci.url || '').indexOf('navidium-shipping-protection') !== -1) return;
        var prev = null;
        for (var k = 0; k < items.length; k++) {
          if (items[k].variant_id === ci.variant_id) { prev = items[k]; break; }
        }
        rebuilt.push({
          name: ci.product_title || ci.title || '',
          sku: ci.sku || '',
          quantity: ci.quantity,
          grams: ci.grams,
          price: ci.original_price,
          vendor: ci.vendor || '',
          product_id: ci.product_id,
          variant_id: ci.variant_id,
          inventory_quantity: prev ? prev.inventory_quantity : 1,
          inventory_policy: prev ? prev.inventory_policy : ''
        });
      });
      rebuilt.forEach(function (item) {
        item.out_of_stock = item.inventory_policy === 'continue' &&
          item.inventory_quantity < 1 &&
          jadcoSkus.indexOf(item.sku) !== -1;
      });
      return rebuilt;
    }

    function shippingItemsIdentity(shippingItems) {
      // Only fields sent to the shipping Worker; protection price, cart total and opt-out are absent.
      return JSON.stringify(shippingItems.map(function (item) {
        return JSON.stringify([String(item.variant_id), String(item.product_id), item.name || '',
          item.sku || '', item.quantity, item.grams, item.price, item.vendor || '', !!item.out_of_stock]);
      }).sort());
    }

    function rebuildItems(cartItems) {
      if (!cartItems) return false;
      items = shippingItemsFromCart(cartItems);
      var allSameDayNow = items.every(function (item) {
        return SAME_DAY_VENDORS.indexOf((item.vendor || '').toLowerCase()) !== -1 &&
          item.inventory_quantity > 0;
      });
      container.classList.toggle('cart-delivery-estimate--same-day', allSameDayNow);
      return items.length > 0;
    }

    var reQuoteTimer = null;
    document.addEventListener('cart:updated', function (e) {
      var cart = e && e.detail && e.detail.cart;
      // Our protection add/remove/tier updates carry a full cart snapshot. Keep the current
      // result and in-flight newest-destination quote when physical shipping inputs are unchanged.
      // Unknown snapshots still use the existing immediate invalidation/read path below.
      if (cart && Array.isArray(cart.items) && lastQuoteLocation && !cartRefreshPending &&
          (cart.currency || currency) === currency &&
          !quoteLocationChanged(lastQuoteLocation, lastQuoteSavedLocation) &&
          shippingItemsIdentity(shippingItemsFromCart(cart.items)) === shippingItemsIdentity(items)) return;
      // Invalidate immediately, before the debounce or asynchronous cart read.
      // A reply for the old quantity must never signal a paid/free transition.
      var revision = ++cartRevision;
      cartQuoteSeq++;
      cartRefreshPending = true;
      if (getSavedLocation()) showCartState('loading');
      clearTimeout(reQuoteTimer);
      reQuoteTimer = setTimeout(function () {
        if (revision !== cartRevision) return;
        function proceed(cartItems) {
          if (revision !== cartRevision ||
              document.querySelector('[data-cart-delivery-estimate]') !== container) return;
          cartRefreshPending = false;
          // Refresh physical items even while IP detection is still pending.
          var hasItems = rebuildItems(cartItems);
          var loc = getSavedLocation();
          if (!loc) { showCartState('prompt'); return; }
          if (hasItems) fetchCartEstimate(loc);
          else showCartState('prompt');
        }
        if (cart && cart.items) {
          proceed(cart.items);
        } else {
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          fetch(root + 'cart.js')
            .then(function (r) { return r.json(); })
            .then(function (c) { proceed(c.items); })
            .catch(function () {
              if (revision === cartRevision) cartRefreshPending = false;
              /* leave the current estimate in place */
            });
        }
      }, 400);
    });

    var saved = getSavedLocation();
    if (saved) {
      fetchCartEstimate(saved);
    } else {
      // No saved location: try IP auto-detection before falling back to the manual prompt.
      showCartState('prompt');
      cancelAutoDetection = detectLocationUnlessManual(container, function (loc) {
        if (loc) {
          saveLocation(loc, true);
          fetchCartEstimate(loc);
        } else {
          showCartState('prompt');
        }
      });
    }
  }

  // ── Init ──

  function init() {
    document.querySelectorAll('[data-delivery-estimate][data-context="pdp"]').forEach(initPdp);
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
// EndCustomCode
